function updateHeaderCartCount(itemCount) {
  const cartLink = document.querySelector('.site-header__cart');
  if (!cartLink) return;

  let count = cartLink.querySelector('.site-header__cart-count');

  if (!count) {
    count = document.createElement('span');
    count.className = 'site-header__cart-count';
    cartLink.append(count);
  }

  count.textContent = itemCount;
  count.hidden = itemCount < 1;
}

const productVariantStates = new WeakMap();

async function replaceProductSectionWithTransition(currentSection, freshSection, replace = () => currentSection.replaceWith(freshSection)) {
  // A size change must not crossfade snapshots of the entire product page.
  // Keep decoded images when their source is unchanged, while refreshing all
  // variant-dependent markup (including price, availability and payment terms).
  if (currentSection.dataset.productUrl === freshSection.dataset.productUrl) {
    const images = [...currentSection.querySelectorAll('[data-media-gallery] img')];
    freshSection.querySelectorAll('[data-media-gallery] img').forEach((freshImage) => {
      const index = images.findIndex((image) => ['src', 'srcset', 'sizes', 'alt'].every(
        (attribute) => image.getAttribute(attribute) === freshImage.getAttribute(attribute),
      ));
      if (index !== -1) freshImage.replaceWith(images.splice(index, 1)[0]);
    });
  }
  replace();
}

async function getCartState(url, errorMessage) {
  const response = await fetch(url, {
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(errorMessage);
  return response.json();
}

function initializeProductForm(container) {
  if (!container || container.dataset.productFormReady === 'true') return;

  const form = container.querySelector('form[action*="/cart/add"]');
  const confirmation = container.querySelector('[data-product-confirmation]');
  const status = container.querySelector('[data-product-form-status]');

  if (!form || !confirmation) return;

  container.dataset.productFormReady = 'true';

  confirmation.querySelectorAll('[data-product-confirmation-close]').forEach((button) => {
    button.addEventListener('click', () => confirmation.close());
  });

  confirmation.addEventListener('click', (event) => {
    if (event.target === confirmation) confirmation.close();
  });

  form.addEventListener('submit', async (event) => {
    const productSection = form.closest('[data-product-section]');
    const revision = productVariantStates.get(productSection)?.revision;
    const canSubmit = () => !productSection || (
      productSection.isConnected && productSection.getAttribute('aria-busy') !== 'true' &&
      productVariantStates.get(productSection)?.revision === revision
    );
    if (!canSubmit()) {
      event.preventDefault();
      return;
    }
    if (event.submitter && event.submitter.name !== 'add') return;

    event.preventDefault();

    const submitButton = form.querySelector('[name="add"]');
    if (!submitButton || form.getAttribute('aria-busy') === 'true') return;
    const buyNow = event.submitter?.hasAttribute('data-buy-now');
    const buyNowButton = form.querySelector('[data-buy-now]');

    const returnFocus = form.temporiSubmitTrigger || submitButton;
    form.setAttribute('aria-busy', 'true');
    submitButton.disabled = true;
    if (buyNowButton) buyNowButton.disabled = true;
    if (status) status.textContent = '';

    try {
      const quantityInput = form.elements?.namedItem('quantity');
      if (quantityInput?.matches('[data-quantity-rule-input]') && window.TemporiQuantityRules) {
        const cart = await getCartState(container.dataset.cartStateUrl, container.dataset.quantityError);
        if (!canSubmit()) return;
        window.TemporiQuantityRules.applyCart(cart);
        if (!window.TemporiQuantityRules.valid(Number(quantityInput.value), window.TemporiQuantityRules.ruleFor(quantityInput), Number(quantityInput.dataset.cartQuantity))) {
          quantityInput.focus();
          throw new Error(container.dataset.quantityError);
        }
      }
      if (buyNow || container.dataset.afterAdd === 'cart') {
        const response = await fetch(container.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || result.message || container.dataset.addError);
        }

        window.location.assign(buyNow ? (window.Shopify?.routes?.root || '/') + 'checkout' : container.dataset.cartUrl);
        return;
      }

      if (window.NavelisCart) {
        await window.NavelisCart.add(new FormData(form), returnFocus);
        return;
      }

      const response = await fetch(container.dataset.cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(form),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || container.dataset.addError);
      }

      const cart = await getCartState(container.dataset.cartStateUrl, container.dataset.cartRefreshError).catch(() => {
        throw new Error(container.dataset.cartRefreshError);
      });
      updateHeaderCartCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
      confirmation.showModal();
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      form.removeAttribute('aria-busy');
      submitButton.disabled = container.dataset.variantAvailable !== 'true' || container.dataset.quantityBlocked === 'true';
      if (buyNowButton) buyNowButton.disabled = submitButton.disabled;
    }
  });
}

function initializeQuantitySelector(selector) {
  if (!selector || selector.dataset.quantityReady === 'true') return;

  const input = selector.querySelector('input[type="number"]');
  if (!input) return;

  selector.dataset.quantityReady = 'true';

  selector.querySelectorAll('[data-quantity-change]').forEach((button) => {
    button.addEventListener('click', () => {
      const step = Number(input.step) || 1;
      const minimum = Number(input.min) || step;
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const direction = button.dataset.quantityChange === 'increase' ? 1 : -1;
      const nextValue = Math.min(maximum, Math.max(minimum, (Number(input.value) || minimum) + direction * step));

      input.value = nextValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

function formatRecipientDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function initializeGiftCardRecipient(recipient) {
  if (!recipient || recipient.dataset.recipientReady === 'true') return;

  const toggle = recipient.querySelector('[data-recipient-toggle]');
  const fields = recipient.querySelector('[data-recipient-fields]');
  const inputs = [...recipient.querySelectorAll('[data-recipient-input]')];
  const email = recipient.querySelector('[data-recipient-email]');
  const sendOn = recipient.querySelector('[data-recipient-date]');
  const offset = recipient.querySelector('[data-recipient-offset]');
  if (!toggle || !fields) return;

  recipient.dataset.recipientReady = 'true';
  if (offset) offset.value = String(new Date().getTimezoneOffset());

  if (sendOn) {
    const today = new Date();
    const latest = new Date(today);
    latest.setDate(latest.getDate() + 90);
    sendOn.min = formatRecipientDate(today);
    sendOn.max = formatRecipientDate(latest);
  }

  const synchronize = () => {
    fields.hidden = !toggle.checked;
    inputs.forEach((input) => {
      input.disabled = !toggle.checked;
    });
    if (email) email.required = toggle.checked;
  };

  toggle.addEventListener('change', synchronize);
  synchronize();
}

function pauseInactiveMedia(gallery, activeMediaId) {
  gallery.querySelectorAll('[data-media-id]').forEach((mediaItem) => {
    if (mediaItem.dataset.mediaId === activeMediaId) return;
    mediaItem.querySelectorAll('video').forEach((video) => video.pause());
    mediaItem.querySelectorAll('iframe').forEach((frame) => {
      let url;
      try { url = new URL(frame.src); } catch { return; }
      let command;
      if (['www.youtube.com', 'www.youtube-nocookie.com'].includes(url.hostname)) {
        command = { event: 'command', func: 'pauseVideo', args: [] };
      } else if (url.hostname === 'player.vimeo.com') {
        command = { method: 'pause' };
      }
      if (command) frame.contentWindow?.postMessage(JSON.stringify(command), url.origin);
    });
  });
}

function initializeMediaGallery(gallery) {
  if (!gallery || gallery.dataset.mediaGalleryReady === 'true') return;

  gallery.dataset.mediaGalleryReady = 'true';
  let mediaSelection = 0;
  const warmGalleryImage = async (item) => {
    const image = item?.querySelector('img');
    if (!image) return;
    image.loading = 'eager';
    try { await image.decode(); } catch { /* Keep the existing view on failed media. */ }
  };
  const preloadGallery = () => {
    if (!gallery.isConnected || navigator.connection?.saveData) return;
    [...gallery.querySelectorAll('[data-media-id]')].slice(0, 5).forEach(warmGalleryImage);
  };
  if ('requestIdleCallback' in window) requestIdleCallback(preloadGallery, { timeout: 1500 });
  else setTimeout(preloadGallery, 600);

  gallery.querySelectorAll('[data-media-thumbnail]').forEach((thumbnail) => {
    thumbnail.addEventListener('click', async () => {
      const mediaId = thumbnail.dataset.mediaThumbnail;
      const selection = ++mediaSelection;
      const target = [...gallery.querySelectorAll('[data-media-id]')].find(item => item.dataset.mediaId === mediaId);
      await warmGalleryImage(target);
      if (selection !== mediaSelection || !gallery.isConnected) return;
      const targetImage = target?.querySelector('img');
      if (targetImage && !targetImage.naturalWidth) return;

      gallery.querySelectorAll('[data-media-id]').forEach((mediaItem) => {
        const isActive = mediaItem.dataset.mediaId === mediaId;
        mediaItem.hidden = false;
        mediaItem.classList.toggle('is-active', isActive);
        mediaItem.setAttribute('aria-hidden', String(!isActive));
        mediaItem.toggleAttribute('inert', !isActive);
      });

      gallery.querySelectorAll('[data-media-thumbnail]').forEach((button) => {
        button.setAttribute('aria-pressed', button === thumbnail ? 'true' : 'false');
      });

      pauseInactiveMedia(gallery, mediaId);
    });
  });
}

function initializeProductLightbox(section) {
  const dialog = section.querySelector('[data-product-lightbox]');
  if (!dialog || dialog.dataset.productLightboxReady === 'true') return;
  dialog.dataset.productLightboxReady = 'true';

  const items = [...dialog.querySelectorAll('[data-product-lightbox-item]')];
  const counter = dialog.querySelector('[data-product-lightbox-counter]');
  const zoomButton = dialog.querySelector('[data-product-lightbox-zoom]');
  const stage = dialog.querySelector('.product-lightbox__stage');
  let zoomed = false;
  const setZoom = (value) => {
    zoomed = value;
    if (stage) {
      stage.classList.toggle('is-zoomed', value);
      stage.scrollLeft = 0;
      stage.scrollTop = 0;
      stage.tabIndex = value ? 0 : -1;
    }
    if (zoomButton) {
      zoomButton.setAttribute('aria-pressed', String(value));
      zoomButton.textContent = value ? zoomButton.dataset.zoomOut : zoomButton.dataset.zoomIn;
    }
  };
  zoomButton?.addEventListener('click', () => setZoom(!zoomed));
  let activeIndex = 0;
  let trigger = null;

  const show = (index) => {
    if (!items.length) return;
    setZoom(false);
    activeIndex = (index + items.length) % items.length;
    items.forEach((item, itemIndex) => {
      item.hidden = itemIndex !== activeIndex;
    });
    if (counter) counter.textContent = `${activeIndex + 1} / ${items.length}`;
  };

  const close = () => {
    if (dialog.open) dialog.close();
    trigger?.focus();
  };

  section.querySelectorAll('[data-product-lightbox-open]').forEach((button) => {
    button.addEventListener('click', () => {
      trigger = button;
      const requestedIndex = items.findIndex((item) => item.dataset.productLightboxItem === button.dataset.productLightboxOpen);
      show(requestedIndex < 0 ? 0 : requestedIndex);
      if (!dialog.open) dialog.showModal();
      dialog.querySelector('[data-product-lightbox-close]')?.focus();
    });
  });

  dialog.querySelector('[data-product-lightbox-close]')?.addEventListener('click', close);
  dialog.querySelector('[data-product-lightbox-previous]')?.addEventListener('click', () => show(activeIndex - 1));
  dialog.querySelector('[data-product-lightbox-next]')?.addEventListener('click', () => show(activeIndex + 1));
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
    else if (event.target.closest('.product-lightbox__item img')) setZoom(!zoomed);
  });
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  dialog.addEventListener('keydown', (event) => {
    if (zoomed && event.target === stage) return;
    const direction = getComputedStyle(dialog).direction === 'rtl' ? -1 : 1;
    if (event.key === 'ArrowLeft') { event.preventDefault(); show(activeIndex - direction); }
    if (event.key === 'ArrowRight') { event.preventDefault(); show(activeIndex + direction); }
  });
}

function initializeSizeGuides(section) {
  section.querySelectorAll('[data-size-guide-dialog]').forEach((dialog) => {
    if (dialog.dataset.sizeGuideReady === 'true') return;
    dialog.dataset.sizeGuideReady = 'true';
    let trigger = null;
    const close = () => { if (dialog.open) dialog.close(); trigger?.focus(); };

    section.querySelector(`[data-size-guide-open="${CSS.escape(dialog.id)}"]`)?.addEventListener('click', (event) => {
      trigger = event.currentTarget;
      dialog.showModal();
      dialog.querySelector('[data-size-guide-close]')?.focus();
    });
    dialog.querySelector('[data-size-guide-close]')?.addEventListener('click', close);
    dialog.addEventListener('click', (event) => { if (event.target === dialog) close(); });
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  });
}

function initializeStickyAdd(section) {
  const sticky = section.querySelector('[data-product-sticky-add]');
  const primaryButton = section.querySelector('.product-page__add');
  const stickyButton = sticky?.querySelector('[data-product-sticky-submit]');
  if (!sticky || !primaryButton || !stickyButton || sticky.dataset.stickyAddReady === 'true') return;
  sticky.dataset.stickyAddReady = 'true';

  stickyButton.addEventListener('click', () => {
    const form = primaryButton.closest('form');
    if (!form || primaryButton.disabled || section.getAttribute('aria-busy') === 'true') return;
    // requestSubmit dispatches synchronously, including native validation.
    form.temporiSubmitTrigger = stickyButton;
    try {
      form.requestSubmit(primaryButton);
    } finally {
      delete form.temporiSubmitTrigger;
    }
  });

  if (!('IntersectionObserver' in window)) return;
  const observer = new IntersectionObserver(([entry]) => {
    sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
  });
  observer.observe(primaryButton);
}

function selectedOptionValues(section) {
  return [...section.querySelectorAll('[data-product-option]')]
    .map((option) => {
      const select = option.querySelector('[data-option-select]');
      if (select) return select.value;
      return option.querySelector('input[type="radio"]:checked')?.value;
    })
    .filter(Boolean);
}

function selectedProductUrl(section, target) {
  if (target.matches('select')) {
    return target.selectedOptions[0]?.dataset.productUrl || section.dataset.productUrl;
  }

  return target.dataset.productUrl || section.dataset.productUrl;
}

async function updateProductVariant(section, target) {
  window.NavelisColours?.cancel();
  const selectionStartedAt = Date.now();
  const state = productVariantStates.get(section);
  if (!state || !section.isConnected) return;
  const restoreSelection = () => {
    section.querySelectorAll('[data-product-option]').forEach((option, index) => {
      const select = option.querySelector('[data-option-select]');
      if (select) select.value = state.selectedValues[index];
      else option.querySelectorAll('input[type="radio"]').forEach((radio) => {
        radio.checked = radio.value === state.selectedValues[index];
      });
    });
  };
  // A purchase-option render already owns this section; keep its selection intact.
  if (section.getAttribute('aria-busy') === 'true' && !state.request) {
    restoreSelection();
    return;
  }

  state.request?.abort();
  const request = new AbortController();
  state.request = request;
  state.revision += 1;
  const isCurrent = () => state.request === request && !request.signal.aborted && section.isConnected;
  if (!state.purchaseRegions) {
    state.purchaseRegions = [...section.querySelectorAll('[data-product-form-container], [data-product-sticky-add]')]
      .map((element) => ({ element, inert: element.inert }));
    state.purchaseRegions.forEach(({ element }) => { element.inert = true; });
  }

  const optionValues = selectedOptionValues(section);
  const variantStatus = section.querySelector('[data-variant-status]');
  const optionPosition = target.closest('[data-product-option]')?.dataset.optionPosition;
  const requestUrl = new URL(selectedProductUrl(section, target), window.location.origin);

  requestUrl.searchParams.delete('variant');
  requestUrl.searchParams.set('option_values', optionValues.join(','));
  requestUrl.searchParams.set('section_id', section.dataset.sectionId);
  const responseSource = window.NavelisProductResponses?.status(requestUrl.toString()) || 'network';
  section.setAttribute('aria-busy', 'true');
  if (variantStatus) variantStatus.textContent = section.dataset.updatingLabel;

  try {
    let markup;
    if (window.NavelisProductResponses && !section.querySelector('[data-purchase-options]')) {
      markup = await window.NavelisProductResponses.read(requestUrl.toString(), { signal: request.signal, errorMessage: section.dataset.updateError });
    } else {
      const response = await fetch(requestUrl.toString(), { headers: { Accept: 'text/html' }, signal: request.signal });
      if (!response.ok) throw new Error(section.dataset.updateError);
      markup = await response.text();
    }
    const html = new DOMParser().parseFromString(markup, 'text/html');
    if (!isCurrent()) return;
    const freshSection = html.querySelector('[data-product-section]');
    if (!freshSection) throw new Error(section.dataset.updateError);

    freshSection.dataset.variantUpdateMs = String(Date.now() - selectionStartedAt);
    freshSection.dataset.variantResponse = responseSource;
    const selectedVariantId = freshSection.dataset.selectedVariantId;
    const displayUrl = new URL(freshSection.dataset.productUrl, window.location.origin);

    if (selectedVariantId) {
      displayUrl.searchParams.set('variant', selectedVariantId);
    } else {
      displayUrl.searchParams.set('option_values', optionValues.join(','));
    }

    await replaceProductSectionWithTransition(section, freshSection, () => {
      // Commit the form and URL only for the current selection.
      if (!isCurrent()) return;
      window.history.replaceState({}, '', displayUrl.toString());
      section.replaceWith(freshSection);
      initializeProductSection(freshSection);
      window.Shopify?.PaymentButton?.init?.();

      if (optionPosition) {
        const refreshedOption = freshSection.querySelector(`[data-option-position="${optionPosition}"]`);
        const refreshedControl = refreshedOption?.querySelector('[data-option-select], input[type="radio"]:checked');
        refreshedControl?.focus();
      }

      document.dispatchEvent(
        new CustomEvent('product:variant-change', {
          detail: { sectionId: freshSection.dataset.sectionId, variantId: selectedVariantId || null },
        }),
      );
    });
  } catch (error) {
    if (!isCurrent()) return;
    restoreSelection();
    state.purchaseRegions.forEach(({ element, inert }) => { element.inert = inert; });
    state.purchaseRegions = null;
    state.request = null;
    section.removeAttribute('aria-busy');
    if (variantStatus) variantStatus.textContent = error.name === 'AbortError' ? section.dataset.updateError : error.message || section.dataset.updateError;
  }
}

async function updatePurchaseOption(section, control) {
  if (section.getAttribute('aria-busy') === 'true') return;

  const requestUrl = new URL(section.dataset.productUrl, window.location.origin);
  const selectedVariantId = section.dataset.selectedVariantId;
  if (selectedVariantId) requestUrl.searchParams.set('variant', selectedVariantId);
  if (control.value) requestUrl.searchParams.set('selling_plan', control.value);
  requestUrl.searchParams.set('section_id', section.dataset.sectionId);
  section.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(requestUrl.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(section.dataset.updateError);

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshSection = html.querySelector('[data-product-section]');
    if (!freshSection) throw new Error(section.dataset.updateError);

    requestUrl.searchParams.delete('section_id');
    window.history.replaceState({}, '', requestUrl.toString());
    await replaceProductSectionWithTransition(section, freshSection);
    initializeProductSection(freshSection);
    window.Shopify?.PaymentButton?.init?.();
    freshSection.querySelector(`[data-purchase-option][value="${CSS.escape(control.value)}"]`)?.focus();

    document.dispatchEvent(
      new CustomEvent('product:purchase-option-change', {
        detail: {
          sectionId: freshSection.dataset.sectionId,
          variantId: selectedVariantId || null,
          sellingPlanId: control.value || null,
        },
      }),
    );
  } catch (error) {
    section.removeAttribute('aria-busy');
    const status = section.querySelector('[data-variant-status], [data-product-form-status]');
    if (status) status.textContent = error.message || section.dataset.updateError;
  }
}

function initializePurchaseOptions(options, section) {
  if (!options || options.dataset.purchaseOptionsReady === 'true') return;

  options.dataset.purchaseOptionsReady = 'true';
  options.querySelectorAll('[data-purchase-option]').forEach((control) => {
    control.addEventListener('change', () => updatePurchaseOption(section, control));
  });
}

function initializeVariantPicker(picker, section) {
  if (!picker || picker.dataset.variantPickerReady === 'true') return;

  picker.dataset.variantPickerReady = 'true';
  productVariantStates.set(section, { selectedValues: selectedOptionValues(section), revision: 0 });
  picker.querySelectorAll('select, input[type="radio"]').forEach((control) => {
    control.addEventListener('change', () => updateProductVariant(section, control));
  });
}

function anticipateProductOptions(section) {
  const cache = window.NavelisProductResponses;
  if (!cache || !cache.allowed() || section.querySelector('[data-purchase-options]')) return;
  const options = [...section.querySelectorAll('[data-product-option]')];
  if (!options.length) return;
  const urlFor = (values, productUrl = section.dataset.productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', values.join(','));
    url.searchParams.set('section_id', section.dataset.sectionId);
    return url.href;
  };
  cache.prime(urlFor(selectedOptionValues(section)), section.outerHTML);
  const warm = (control, index) => {
    if (!section.isConnected || section.getAttribute('aria-busy') === 'true') return;
    const values = selectedOptionValues(section); values[index] = control.value;
    return cache.prefetch(urlFor(values, control.dataset.productUrl || section.dataset.productUrl));
  };
  options.forEach((option, index) => {
    option.querySelectorAll('input[type="radio"]').forEach(control => {
      control.addEventListener('focus', () => warm(control, index));
      control.labels?.[0]?.addEventListener('pointerenter', () => warm(control, index));
    });
  });
  // At most two alternatives on a small, single-option product; never expand
  // a whole high-variant product or a catalogue in the background.
  if (options.length === 1) {
    const controls = [...options[0].querySelectorAll('input[type="radio"], select option')];
    if (controls.length <= 8) {
      const nearby = controls.filter(control => control.value !== selectedOptionValues(section)[0]).slice(0, 2);
      const schedule = window.requestIdleCallback || (callback => setTimeout(callback, 300));
      schedule(() => { for (const control of nearby) warm(control, 0); });
    }
  }
}

function initializeProductSection(section) {
  if (!section) return;

  anticipateProductOptions(section);
  window.NavelisColours?.schedule();
  initializeMediaGallery(section.querySelector('[data-media-gallery]'));
  initializeProductLightbox(section);
  initializeSizeGuides(section);
  initializeStickyAdd(section);
  initializeVariantPicker(section.querySelector('[data-variant-picker]'), section);
  initializeQuantitySelector(section.querySelector('[data-quantity-selector]'));
  initializePurchaseOptions(section.querySelector('[data-purchase-options]'), section);
  section.querySelectorAll('[data-gift-card-recipient]').forEach(initializeGiftCardRecipient);
  section.querySelectorAll('[data-product-form-container]').forEach(initializeProductForm);
}

document.querySelectorAll('[data-product-section]').forEach(initializeProductSection);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-product-section]').forEach(initializeProductSection);
});
